import React, { useState, useEffect, useCallback, useRef, memo } from "react";
import { Download, Filter, Calendar, Clock, FileText } from "lucide-react";
import SafeTrendingUp from "../Common/SafeTrendingUp";
import Filters from "./Filters";
import DatePicker from "../Common/DatePicker";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import { employeeService, attendanceService, attendanceStructureService } from "../../services/api";
import {
  formatCompOffDate,
  formatCompOffDays,
  formatCompOffSource,
  formatCompOffStatus,
} from "../../lib/compOffLedger";
import AttendanceFilteredReportPanel from "./attendance/reports/AttendanceFilteredReportPanel";

/** Reports using Filters.jsx + Load + Excel/PDF panel (replaces legacy daily/leave UI). */
const FILTERED_PANEL_REPORTS = new Set([
  "attendance-checks",
  "daily-attendance",
  "who-are-in",
  "leave-report",
]);

const ATTENDANCE_REPORT_SIDEBAR = [
  { id: "attendance-checks", label: "Attendance Checks", icon: Clock },
  { id: "daily-attendance", label: "Daily Attendance Report", icon: Calendar },
  { id: "who-are-in", label: "Who are in", icon: Clock },
  { id: "monthly-attendance", label: "Monthly Attendance Report", icon: FileText },
  { id: "leave-report", label: "Leave Balance Report", icon: Clock },
  { id: "overtime-report", label: "Overtime Report", icon: SafeTrendingUp },
  { id: "lop-reversal-report", label: "LOP Reversal Report", icon: FileText },
  { id: "comp-off-ledger", label: "Comp Off Ledger Report", icon: FileText },
];

/**
 * Attendance Reports component that displays and manages:
 * 1. Daily Attendance Report
 * 2. Monthly Attendance Report
 * 3. Leave Report
 * 4. Overtime Report
 */
const AttendanceReports = memo(
  function AttendanceReports({
    darkMode,
    setCurrentPage,
    toggleDarkMode,
    forcedSubTab = null,
    onSubTabChange = null,
    hideSidebar = false,
  }) {
    const [activeSubTab, setActiveSubTab] = useState(forcedSubTab || "daily-attendance");
    const [selectedYear, setSelectedYear] = useState("2024-25");
    const [showFilterPopup, setShowFilterPopup] = useState(false);
    const [showEmployeeList, setShowEmployeeList] = useState(false);
    const [baseEmployees, setBaseEmployees] = useState([]);
    const [loadedEmployees, setLoadedEmployees] = useState([]);
    const [filtersApplied, setFiltersApplied] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState(null);
    const [dateRange, setDateRange] = useState({ from: "", to: "" });
    const [selectedMonth, setSelectedMonth] = useState("");
    const [monthlyAttendance, setMonthlyAttendance] = useState({});
    const [monthlyLoaded, setMonthlyLoaded] = useState(false);
    const [lopReversalReportMonth, setLOPReversalReportMonth] = useState(null);
    const [lopReversalReportYear, setLOPReversalReportYear] = useState(null);
    const [lopReversalReportData, setLOPReversalReportData] = useState([]);
    const [lopReversalReportLoading, setLOPReversalReportLoading] = useState(false);
    /** True after a fetch finishes for the current payroll month (including empty results). */
    const [lopReversalReportFetched, setLOPReversalReportFetched] = useState(false);
    const [lopReversalMinDate, setLOPReversalMinDate] = useState(null);
    const [compOffLedgerRows, setCompOffLedgerRows] = useState([]);
    const [compOffLedgerLoading, setCompOffLedgerLoading] = useState(false);
    const [compOffLedgerFetched, setCompOffLedgerFetched] = useState(false);
    const [compOffDateRange, setCompOffDateRange] = useState({ from: "", to: "" });
    const lopReversalFetchSeqRef = useRef(0);
    const monthlyAutoLoadRef = useRef("");

    useEffect(() => {
      if (!forcedSubTab) return;
      setActiveSubTab(forcedSubTab);
    }, [forcedSubTab]);

    const handleSubTabSelect = useCallback(
      (subTabId) => {
        setActiveSubTab(subTabId);
        if (typeof onSubTabChange === "function") {
          onSubTabChange(subTabId);
        }
      },
      [onSubTabChange]
    );

    /** Parse yyyy-mm-dd without UTC shift (ISO date-only strings parse as UTC and break getMonth in some zones). */
    const parseYearMonthFromPickerValue = (raw) => {
      const s = String(raw || "").trim();
      const m = s.match(/^(\d{4})-(\d{2})/);
      if (!m) return null;
      const year = Number(m[1]);
      const month = Number(m[2]);
      if (!year || month < 1 || month > 12) return null;
      return { year, month };
    };

    const toISODate = (dateObj) => {
      const d = new Date(dateObj);
      if (isNaN(d.getTime())) return "";
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    };

    const addDays = (dateObj, days) => {
      const d = new Date(dateObj);
      d.setDate(d.getDate() + days);
      return d;
    };

    const loadLOPReversalReport = useCallback(
      async (month, year) => {
        const fetchSeq = ++lopReversalFetchSeqRef.current;
        if (typeof attendanceService.getLOPReversalReport !== "function") {
          toast.error("LOP Reversal report is not available.");
          setLOPReversalReportFetched(true);
          setLOPReversalReportData([]);
          return;
        }
        setLOPReversalReportLoading(true);
        setLOPReversalReportFetched(false);
        try {
          const res = await attendanceService.getLOPReversalReport(month, year);
          if (fetchSeq !== lopReversalFetchSeqRef.current) return;
          const arr = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
          setLOPReversalReportData(arr);
        } catch (err) {
          if (fetchSeq !== lopReversalFetchSeqRef.current) return;
          console.error(err);
          setLOPReversalReportData([]);
          toast.error("Failed to load LOP reversal report.");
        } finally {
          if (fetchSeq === lopReversalFetchSeqRef.current) {
            setLOPReversalReportLoading(false);
            setLOPReversalReportFetched(true);
          }
        }
      },
      []
    );

    const loadCompOffLedgerReport = useCallback(async (from, to) => {
      setCompOffLedgerLoading(true);
      setCompOffLedgerFetched(false);
      try {
        const params = { limit: 5000 };
        if (from) params.fromDate = from;
        if (to) params.toDate = to;
        const res = await attendanceService.listCompOffLedger(params);
        const arr = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        setCompOffLedgerRows(arr);
      } catch (err) {
        console.error(err);
        setCompOffLedgerRows([]);
        toast.error("Failed to load Comp Off ledger report.");
      } finally {
        setCompOffLedgerLoading(false);
        setCompOffLedgerFetched(true);
      }
    }, []);

    const prevSubTabRef = useRef(activeSubTab);
    useEffect(() => {
      const prev = prevSubTabRef.current;
      prevSubTabRef.current = activeSubTab;
      if (activeSubTab === "lop-reversal-report" && prev !== "lop-reversal-report") {
        lopReversalFetchSeqRef.current += 1;
        setLOPReversalReportMonth(null);
        setLOPReversalReportYear(null);
        setLOPReversalReportData([]);
        setLOPReversalReportFetched(false);
        setLOPReversalReportLoading(false);
      }
    }, [activeSubTab]);

    useEffect(() => {
      if (activeSubTab !== "comp-off-ledger") return;
      void loadCompOffLedgerReport(compOffDateRange.from, compOffDateRange.to);
    }, [activeSubTab, loadCompOffLedgerReport]);

    useEffect(() => {
      if (activeSubTab !== "lop-reversal-report") return;
      if (lopReversalReportMonth == null || lopReversalReportYear == null) {
        lopReversalFetchSeqRef.current += 1;
        setLOPReversalReportData([]);
        setLOPReversalReportFetched(false);
        setLOPReversalReportLoading(false);
        return;
      }
      loadLOPReversalReport(lopReversalReportMonth, lopReversalReportYear);
    }, [activeSubTab, lopReversalReportMonth, lopReversalReportYear, loadLOPReversalReport]);

    useEffect(() => {
      try {
        const raw = localStorage.getItem("companyData");
        if (!raw) return;
        const companyData = typeof raw === "string" ? JSON.parse(raw) : raw;
        const commencesOn = companyData?.serviceCommencesOn;
        if (!commencesOn) return;
        const d = new Date(commencesOn);
        if (!isNaN(d.getTime())) setLOPReversalMinDate(d);
      } catch (e) {
        console.warn("AttendanceReports minDate from companyData.serviceCommencesOn:", e);
      }
    }, []);

    useEffect(() => {
      if (!lopReversalMinDate) return;
      if (lopReversalReportMonth == null || lopReversalReportYear == null) return;
      const minYear = lopReversalMinDate.getFullYear();
      const minMonth = lopReversalMinDate.getMonth() + 1;
      if (
        lopReversalReportYear < minYear ||
        (lopReversalReportYear === minYear && lopReversalReportMonth < minMonth)
      ) {
        setLOPReversalReportYear(minYear);
        setLOPReversalReportMonth(minMonth);
      }
    }, [lopReversalMinDate, lopReversalReportMonth, lopReversalReportYear]);

    useEffect(() => {
      if (activeSubTab !== "monthly-attendance") return;
      if (!selectedMonth) {
        monthlyAutoLoadRef.current = "";
        return;
      }
      const key = `${activeSubTab}:${selectedMonth}`;
      if (monthlyAutoLoadRef.current === key) return;
      monthlyAutoLoadRef.current = key;
      void loadAttendanceData();
    }, [activeSubTab, selectedMonth]);

    const todayISO = toISODate(new Date());
    const companyMinISO = lopReversalMinDate ? toISODate(lopReversalMinDate) : undefined;
    const dailyFromMinISO = (() => {
      if (!dateRange.to) return companyMinISO;
      const to = new Date(dateRange.to);
      const minByRange = toISODate(addDays(to, -30));
      if (!companyMinISO) return minByRange;
      return companyMinISO > minByRange ? companyMinISO : minByRange;
    })();
    const dailyFromMaxISO = (() => {
      if (!dateRange.to) return todayISO;
      return dateRange.to > todayISO ? todayISO : dateRange.to;
    })();
    const dailyToMinISO = (() => {
      if (!dateRange.from) return companyMinISO;
      return dateRange.from;
    })();
    const dailyToMaxISO = (() => {
      if (!dateRange.from) return todayISO;
      const from = new Date(dateRange.from);
      const maxByRange = toISODate(addDays(from, 30));
      return maxByRange < todayISO ? maxByRange : todayISO;
    })();

    // Employees are provided by Filters -> onApply

    const loadAttendanceData = async () => {
      const isDaily = activeSubTab === "daily-attendance";
      const isMonthly = activeSubTab === "monthly-attendance";

      // Validate dates for daily report
      if (isDaily) {
        if (!dateRange.from || !dateRange.to) {
          toast.error("Please select From and To dates (max 31 days)");
          return;
        }
        const fromD = new Date(dateRange.from);
        const toD = new Date(dateRange.to);
        const diffDays =
          Math.floor((toD.getTime() - fromD.getTime()) / (1000 * 60 * 60 * 24)) +
          1;
        if (diffDays > 31) {
          toast.error("Date range cannot exceed 31 days");
          return;
        }
      }

      // Use current employees; if none loaded yet, fetch first so we can run both in one Load click
      let employeesToUse = baseEmployees;
      if (baseEmployees.length === 0) {
        try {
          const resp = await employeeService.getEmployees({ excludeEmploymentTypes: "External,External" });
          let employeesData = [];
          if (!resp) employeesData = [];
          else if (Array.isArray(resp)) employeesData = resp;
          else if (resp.data && Array.isArray(resp.data)) employeesData = resp.data;
          else if (resp.employees && Array.isArray(resp.employees))
            employeesData = resp.employees;
          else if (resp.success && Array.isArray(resp.data)) employeesData = resp.data;
          setBaseEmployees(employeesData);
          setLoadedEmployees(employeesData);
          employeesToUse = employeesData; // use fresh data in this same run (state not updated yet)
        } catch (err) {
          console.error("Failed to load employees for summary", err);
          toast.error("Unable to load employees for summary");
          return;
        }
      }

      // Monthly flow: load employees (if needed) then fetch attendance in one Load click
      if (isMonthly) {
        if (!selectedMonth) {
          toast.error("Please choose a month");
          return;
        }
        const [y, m] = selectedMonth.split("-");
        const monthNum = Number(m);
        const yearNum = Number(y);
        if (!monthNum || !yearNum) {
          toast.error("Invalid month selected");
          return;
        }
        try {
          const ids = employeesToUse.map((e) => e.id || e.employeeId || e.empCode).filter(Boolean);
          if (ids.length === 0) {
            toast.error("No employees found. Cannot load attendance.");
            return;
          }
          const resp = await attendanceService.getAttendanceHistory(
            monthNum,
            yearNum,
            1,
            50000,
            "",
            ids
          );
          // Robust API response parsing: handle nested structures like { data: { attendance: {...} } }, { data: [...] }, { attendance: {...} }, etc.
          let att = {};
          let rawData = resp;
          // Unwrap nested response structures
          if (rawData && typeof rawData === "object" && !Array.isArray(rawData)) {
            if (rawData.data?.attendance) rawData = rawData.data.attendance;
            else if (rawData.attendance) rawData = rawData.attendance;
            else if (rawData.data) rawData = rawData.data;
          }
          if (Array.isArray(rawData)) {
            // Array of attendance records: convert to map keyed by multiple employee identifiers
            rawData.forEach((rec) => {
              if (!rec) return;
              // Try all possible employee ID fields from the record
              const possibleIds = [
                rec.employeeId,
                rec.employee_id,
                rec.empId,
                rec.emp_id,
                rec.empCode,
                rec.emp_code,
                rec.code,
                rec.id,
              ].filter((v) => v != null && v !== "");
              // Parse day from date field
              let day = null;
              if (rec.date) {
                const d = new Date(rec.date);
                if (!isNaN(d.getTime())) day = String(d.getDate());
              } else if (rec.day != null) {
                day = String(rec.day);
              }
              if (!day) return;
              const status = rec.status || rec.attendanceStatus || rec.code || "";
              // Store under all possible keys so lookup succeeds
              possibleIds.forEach((pid) => {
                const key = String(pid);
                if (!att[key]) att[key] = {};
                att[key][day] = status;
              });
            });
          } else if (rawData && typeof rawData === "object") {
            // Object format: could be { empId: { day: status } } or { empId: { "YYYY-MM-DD": status } }
            // Normalize all date keys to day-of-month numbers
            Object.entries(rawData).forEach(([empKey, daysObj]) => {
              if (!daysObj || typeof daysObj !== "object") return;
              if (!att[empKey]) att[empKey] = {};
              Object.entries(daysObj).forEach(([dayKey, status]) => {
                let dayNum = dayKey;
                // If dayKey looks like a date string (contains "-" or "/"), parse it
                if (typeof dayKey === "string" && (dayKey.includes("-") || dayKey.includes("/"))) {
                  const d = new Date(dayKey);
                  if (!isNaN(d.getTime())) {
                    dayNum = String(d.getDate());
                  }
                }
                att[empKey][String(dayNum)] = status;
              });
            });
          }
          setMonthlyAttendance(att || {});
          setMonthlyLoaded(true);
          setIsLoaded(true);
          toast.success("Monthly attendance summary loaded");
          return;
        } catch (err) {
          console.error("Monthly attendance fetch failed", err);
          toast.error("Failed to load monthly attendance");
          return;
        }
      }

      setIsLoaded(true);
      if (isDaily) {
        toast.success("Summary loaded for the selected date range");
      } else {
        setShowEmployeeList(true);
        toast.success("Employee data loaded successfully!");
      }
    };

    const buildDateList = () => {
      if (!dateRange.from || !dateRange.to) return [];
      const start = new Date(dateRange.from);
      const end = new Date(dateRange.to);
      const out = [];
      for (
        let d = new Date(start.getTime());
        d <= end;
        d.setDate(d.getDate() + 1)
      ) {
        out.push(new Date(d.getTime()));
      }
      return out;
    };

    const fetchAttendanceForRange = async (employeeIds) => {
      const dates = buildDateList();
      if (dates.length === 0 || employeeIds.length === 0) return {};

      // Group dates by month-year to minimize calls
      const monthGroups = {};
      dates.forEach((d) => {
        const key = `${d.getMonth() + 1}-${d.getFullYear()}`;
        if (!monthGroups[key]) monthGroups[key] = { month: d.getMonth() + 1, year: d.getFullYear() };
      });

      const attendanceMap = {};
      for (const key of Object.keys(monthGroups)) {
        const { month, year } = monthGroups[key];
        try {
          const resp = await attendanceService.getChunkedAttendance(
            month,
            year,
            1,
            500,
            "",
            { employeeIds: employeeIds.join(",") }
          );
          let att = resp?.attendance || resp?.data || resp || {};
          // Normalize array payload to map
          if (Array.isArray(att)) {
            const attMap = {};
            att.forEach((rec) => {
              const empId = String(rec.employee_id || rec.employeeId || rec.id);
              const day = new Date(rec.date).toISOString().slice(0, 10);
              if (!attMap[empId]) attMap[empId] = {};
              attMap[empId][day] = rec.status || rec.attendanceStatus || rec.code || "";
            });
            att = attMap;
          } else {
            // Convert day-of-month maps to full date strings for this month
            const attMap = {};
            Object.entries(att || {}).forEach(([empId, daysObj]) => {
              Object.entries(daysObj || {}).forEach(([day, status]) => {
                const dayNum = parseInt(day, 10);
                if (!Number.isFinite(dayNum)) return;
                const dateStr = new Date(year, month - 1, dayNum)
                  .toISOString()
                  .slice(0, 10);
                if (!attMap[empId]) attMap[empId] = {};
                attMap[empId][dateStr] = status;
              });
            });
            att = attMap;
          }

          Object.entries(att || {}).forEach(([empId, datesObj]) => {
            if (!attendanceMap[empId]) attendanceMap[empId] = {};
            Object.assign(attendanceMap[empId], datesObj);
          });
        } catch (err) {
          console.error("Attendance fetch failed for month", month, year, err);
        }
      }

      return attendanceMap;
    };

    const exportReport = async (reportType) => {
      const getCompanyCodeForExcel = () => {
        try {
          const raw = localStorage.getItem("companyData");
          const companyData = raw ? (typeof raw === "string" ? JSON.parse(raw) : raw) : null;
          const code = String(companyData?.code || "")
            .trim()
            .replace(/[^A-Za-z0-9]/g, "_")
            .replace(/_+/g, "_")
            .replace(/^_+|_+$/g, "")
            .toUpperCase();
          return code || "COMPANY";
        } catch (_) {
          return "COMPANY";
        }
      };
      const buildExcelFilename = (baseName, monthValue = null, yearValue = null) => {
        const now = new Date();
        let mm = now.getMonth() + 1;
        let yyyy = now.getFullYear();
        if (monthValue && typeof monthValue === "string" && monthValue.includes("-")) {
          const [y, m] = monthValue.split("-");
          const parsedY = Number(y);
          const parsedM = Number(m);
          if (!Number.isNaN(parsedY) && !Number.isNaN(parsedM)) {
            yyyy = parsedY;
            mm = parsedM;
          }
        } else {
          if (Number.isFinite(Number(monthValue)) && Number(monthValue) >= 1 && Number(monthValue) <= 12) {
            mm = Number(monthValue);
          }
          if (Number.isFinite(Number(yearValue)) && Number(yearValue) > 0) {
            yyyy = Number(yearValue);
          }
        }
        const safeName = String(baseName || "Attendance_Report")
          .replace(/[^A-Za-z0-9]+/g, "_")
          .replace(/_+/g, "_")
          .replace(/^_+|_+$/g, "");
        return `${getCompanyCodeForExcel()}_${safeName}_${String(mm).padStart(2, "0")}_${yyyy}.xlsx`;
      };
      const isDaily = activeSubTab === "daily-attendance";
      const isMonthly = activeSubTab === "monthly-attendance";
      const isLOPReversal = activeSubTab === "lop-reversal-report";
      const isCompOffLedger = activeSubTab === "comp-off-ledger";
      const hasEmployees = loadedEmployees.length > 0;

      if (isCompOffLedger) {
        if (!compOffLedgerRows.length) {
          toast.error("No Comp Off ledger rows to export. Load the report first.");
          return;
        }
        try {
          const wsData = [
            [
              "Sl No",
              "Employee Code",
              "Employee Name",
              "Type",
              "Worked Date",
              "Source",
              "Hours Worked",
              "Credit Days",
              "Expires On",
              "Availed On",
              "Remaining Days",
              "Status",
              "Remarks",
            ],
            ...compOffLedgerRows.map((r, idx) => [
              idx + 1,
              r.employeeCode,
              r.employeeName,
              r.movement === "availed" ? "Availed" : "Earned",
              r.sourceDate ? String(r.sourceDate).slice(0, 10) : "",
              formatCompOffSource(r.sourceType),
              r.hoursWorked == null ? "" : Number(r.hoursWorked),
              Number(r.creditDays || 0),
              r.expiresOn ? String(r.expiresOn).slice(0, 10) : "",
              r.availDate ? String(r.availDate).slice(0, 10) : "",
              Number(r.remainingDays || 0),
              formatCompOffStatus(r),
              r.notes || "",
            ]),
          ];
          const ws = XLSX.utils.aoa_to_sheet(wsData);
          ws["!cols"] = wsData[0].map((_, colIdx) => {
            const maxLen = wsData.reduce((m, row) => {
              const val = row[colIdx] == null ? "" : String(row[colIdx]);
              return Math.max(m, val.length);
            }, 0);
            return { wch: Math.min(60, Math.max(10, maxLen + 2)) };
          });
          const wb = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(wb, ws, "Comp Off Ledger");
          XLSX.writeFile(wb, buildExcelFilename("Comp_Off_Ledger_Report"));
          toast.success("Comp Off ledger report downloaded.");
        } catch (err) {
          console.error(err);
          toast.error("Failed to export Comp Off ledger report.");
        }
        return;
      }

      if (isLOPReversal) {
        if (lopReversalReportMonth == null || lopReversalReportYear == null) {
          toast.error("Select a payroll month to export.");
          return;
        }
        if (!lopReversalReportData || lopReversalReportData.length === 0) {
          toast.error("No LOP reversal data to export for the selected payroll month.");
          return;
        }
        try {
          const wsData = [
            ["Sl No", "Employee Code", "Employee Name", "DOJ", "LOP Month", "LOP Reversal Days", "Dates", "Payroll Month"],
            ...lopReversalReportData.map((r, idx) => [
              idx + 1,
              r.employeeCode,
              r.employeeName,
              r.doj ? String(r.doj).slice(0, 10) : "",
              r.lopMonth,
              r.lopReversalDays,
              r.dates,
              r.payrollMonth,
            ]),
          ];
          const ws = XLSX.utils.aoa_to_sheet(wsData);
          // Auto-size columns based on content length
          ws["!cols"] = wsData[0].map((_, colIdx) => {
            const maxLen = wsData.reduce((m, row) => {
              const val = row[colIdx] == null ? "" : String(row[colIdx]);
              return Math.max(m, val.length);
            }, 0);
            return { wch: Math.min(60, Math.max(10, maxLen + 2)) };
          });
          const wb = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(wb, ws, "LOP Reversal Report");
          XLSX.writeFile(
            wb,
            buildExcelFilename(
              "LOP_Reversal_Report",
              lopReversalReportMonth,
              lopReversalReportYear
            )
          );
          toast.success("LOP Reversal report exported.");
        } catch (err) {
          console.error(err);
          toast.error("Failed to export LOP Reversal report.");
        }
        return;
      }

      if (!hasEmployees) {
        toast.error("Please apply filters to load employees for download");
        return;
      }

      if (isDaily) {
        if (!dateRange.from || !dateRange.to) {
          toast.error("Please select From and To dates (max 31 days)");
          return;
        }
        if (!isLoaded) {
          toast.error("Please click Load after selecting dates");
          return;
        }
      }

      if (isMonthly) {
        if (!selectedMonth) {
          toast.error("Please choose a month");
          return;
        }
        if (!isLoaded) {
          toast.error("Please click Load after selecting month");
          return;
        }
      } else if (!isDaily && !showEmployeeList) {
        toast.error("Please load employee data first");
        return;
      }

      try {
        // Helper function to get name by ID from master data
        const getNameById = (id, masterDataArray) => {
          if (!id || !masterDataArray) return "";
          const item = masterDataArray.find(
            (item) => item.id === id || item.id === parseInt(id)
          );
          return item ? item.name : "";
        };

        // Helper function to format dates
        const formatDateForExport = (date) => {
          if (!date) return "";
          try {
            return new Date(date).toLocaleDateString("en-GB"); // dd/mm/yyyy format
          } catch (error) {
            return "";
          }
        };

        // Build meta info rows: prefer real company data from localStorage
        let companyName = "Company Name";
        let companyAddress = "Company Address";
        try {
          const raw = typeof localStorage !== "undefined" ? localStorage.getItem("companyData") : null;
          if (raw) {
            const companyData = typeof raw === "string" ? JSON.parse(raw) : raw;
            if (companyData?.name) companyName = companyData.name;
            if (companyData?.address) companyAddress = companyData.address;
            else if (companyData?.addressLine1) companyAddress = companyData.addressLine1;
          }
        } catch (e) {
          /* ignore */
        }
        if (companyName === "Company Name" && baseEmployees[0]?.company?.name)
          companyName = baseEmployees[0].company.name;
        if (companyName === "Company Name" && baseEmployees[0]?.companyName)
          companyName = baseEmployees[0].companyName;
        if (companyAddress === "Company Address" && baseEmployees[0]?.company?.address)
          companyAddress = baseEmployees[0].company.address;
        if (companyAddress === "Company Address" && baseEmployees[0]?.companyAddress)
          companyAddress = baseEmployees[0].companyAddress;
        const filterSummary = [];
        if (appliedFilters) {
          Object.entries(appliedFilters).forEach(([key, val]) => {
            if (val !== undefined && val !== null && val !== "") {
              filterSummary.push(`${key}: ${val}`);
            }
          });
        }
        const dateRangeLabel = isDaily
          ? dateRange.from && dateRange.to
            ? `${new Date(dateRange.from).toLocaleDateString()} - ${new Date(
                dateRange.to
              ).toLocaleDateString()}`
            : "Not specified"
          : selectedMonth
          ? (() => {
              const [y, m] = selectedMonth.split("-");
              return `${new Date(Number(y), Number(m) - 1, 1).toLocaleString(
                "en-US",
                { month: "long", year: "numeric" }
              )}`;
            })()
          : "Not specified";

        const dateList = isDaily ? buildDateList() : [];

        // For monthly export: fetch leave types and configurations so summary columns match "calculate and show summary" UI
        let statusDefinitions = {
          PR: "Present",
          LOP: "Absent",
          WO: "Weekly Off",
          PH: "Public Holiday",
        };
        let reverseMapping = {};
        let paidCodes = new Set(["PR", "PH", "WO"]);
        let statusCodesOrder = ["PR", "LOP", "WO", "PH"];
        if (activeSubTab === "monthly-attendance") {
          try {
            const [leaveResp, configResp] = await Promise.all([
              attendanceStructureService.getLeaveTypes(),
              attendanceService.getConfigurations().catch(() => null),
            ]);
            // Parse same as main Attendance: { success, data } or array or { leaveTypes }
            let rawLeaveTypes = [];
            if (leaveResp && leaveResp.success && (leaveResp.data || Array.isArray(leaveResp.data)))
              rawLeaveTypes = leaveResp.data || [];
            else if (Array.isArray(leaveResp)) rawLeaveTypes = leaveResp;
            else if (leaveResp?.data) rawLeaveTypes = Array.isArray(leaveResp.data) ? leaveResp.data : [];
            else if (leaveResp?.leaveTypes) rawLeaveTypes = Array.isArray(leaveResp.leaveTypes) ? leaveResp.leaveTypes : [];
            const currentCompanyId = typeof localStorage !== "undefined" ? localStorage.getItem("companyId") : null;
            const leaveTypesList = (rawLeaveTypes || []).filter((lt) => {
              const ltCompanyId = lt.companyId || lt.company_id;
              const isGlobal = !ltCompanyId;
              const isForThisCompany =
                currentCompanyId != null &&
                (ltCompanyId === parseInt(currentCompanyId, 10) || String(ltCompanyId) === String(currentCompanyId));
              const hasRequired = lt.id && (lt.code || lt.shortCode) && lt.name;
              return isGlobal || isForThisCompany || hasRequired;
            });
            leaveTypesList.forEach((lt) => {
              const code = lt.code || lt.shortCode || lt.id;
              if (!code) return;
              const c = String(code).trim().toUpperCase();
              if (!c) return;
              const label = lt.name || lt.label || lt.displayName || c;
              statusDefinitions[c] = label;
              if (!statusCodesOrder.includes(c)) statusCodesOrder.push(c);
              if (lt.isUnpaid !== true) paidCodes.add(c);
            });
            const configs = Array.isArray(configResp)
              ? configResp
              : configResp?.data ?? configResp?.codes ?? [];
            (configs || []).forEach((cfg) => {
              const orig = String(cfg.originalCode || cfg.original_code || "").trim().toUpperCase();
              const custom = String(cfg.customCode || cfg.custom_code || "").trim().toUpperCase();
              if (orig && custom) reverseMapping[custom] = orig;
            });
          } catch (e) {
            console.warn("AttendanceReports: could not load leave types/config for export", e);
          }
        }

        // Map label -> code so API values like "Earned Leave" or "Present" normalize to EL, PR
        let labelToCode = {};
        if (activeSubTab === "monthly-attendance") {
          Object.entries(statusDefinitions).forEach(([code, label]) => {
            if (label && typeof label === "string") {
              const key = label.trim().toUpperCase();
              if (key) labelToCode[key] = code;
            }
          });
        }

        // For monthly-attendance: export only employees active in that month (joined on or before month end; not relieved before month start)
        let employeesToExport = loadedEmployees;
        if (activeSubTab === "monthly-attendance" && selectedMonth) {
          const [y, m] = selectedMonth.split("-");
          const year = Number(y);
          const monthNum = Number(m);
          if (!isNaN(year) && !isNaN(monthNum) && monthNum >= 1 && monthNum <= 12) {
            const monthStart = new Date(year, monthNum - 1, 1);
            const monthEnd = new Date(year, monthNum, 0);
            const parseEmpDate = (val) => {
              if (val == null || val === "") return null;
              const d = new Date(val);
              return isNaN(d.getTime()) ? null : d;
            };
            employeesToExport = loadedEmployees.filter((emp) => {
              const joinDate = parseEmpDate(emp.joinDate || emp.joiningDate);
              if (joinDate && joinDate > monthEnd) return false;
              const relievedDate = parseEmpDate(
                emp.relievedDate || emp.exitDate || emp.relievingDate || emp.resignationDate
              );
              if (relievedDate && relievedDate < monthStart) return false;
              return true;
            });
          }
        }

        // Fetch attendance statuses
        let attendanceStatusMap = {};
        if (isDaily && dateList.length > 0) {
          const ids = loadedEmployees
            .map((e) => e.id || e.employeeId || e.empCode)
            .filter(Boolean);
          attendanceStatusMap = await fetchAttendanceForRange(ids);
        } else if (activeSubTab === "monthly-attendance") {
          attendanceStatusMap = {};
          Object.entries(monthlyAttendance || {}).forEach(([empId, daysObj]) => {
            const map = {};
            Object.entries(daysObj || {}).forEach(([day, status]) => {
              const y = selectedMonth?.split("-")[0];
              const m = selectedMonth?.split("-")[1];
              const fullDate = `${y}-${m}-${String(day).padStart(2, "0")}`;
              map[fullDate] = status;
            });
            attendanceStatusMap[empId] = map;
          });
        }

        // Export data with attendance statuses for daily/monthly; trimmed employee fields
        const exportData = employeesToExport.map((employee, index) => {
          const row = {
            "Sl No": index + 1,
            "Employee Code": employee.empCode || employee.code || "",
            Name: employee.user?.name || employee.name || "",
            Department:
              employee.departments?.name ||
              employee.department?.name ||
              employee.departmentName ||
              "",
            Designation:
              employee.designations?.name ||
              employee.designation?.name ||
              employee.designationName ||
              "",
            Status: employee.employmentStatus || employee.status || "Active",
          };

          if (isDaily && dateList.length > 0) {
            const empId = String(employee.id || employee.employeeId || employee.empCode);
            dateList.forEach((d) => {
              const key = d.toISOString().slice(0, 10);
              const label = d.toLocaleDateString("en-GB"); // DD/MM/YYYY
              const status =
                attendanceStatusMap?.[empId]?.[key] ||
                attendanceStatusMap?.[empId]?.[key.split("T")[0]] ||
                "";
              row[label] = status || "-";
            });
          } else if (activeSubTab === "monthly-attendance") {
            const [y, m] = selectedMonth ? selectedMonth.split("-") : [];
            const daysInMonth = selectedMonth
              ? new Date(Number(y), Number(m), 0).getDate()
              : 31;
            const counts = {};
            statusCodesOrder.forEach((k) => {
              counts[k] = 0;
            });
            // Normalize status to one of statusDefinitions keys (API may return code, custom code, or full label)
            const normalizeStatusForCount = (s) => {
              if (s == null || typeof s !== "string") return null;
              let u = String(s).trim().toUpperCase();
              if (!u) return null;
              if (reverseMapping[u]) u = reverseMapping[u];
              if (Object.prototype.hasOwnProperty.call(statusDefinitions, u)) return u;
              if (labelToCode[u]) return labelToCode[u];
              if (["P", "PS", "PRESENT"].includes(u)) return "PR";
              if (["PHS", "PUBLIC HOLIDAY"].includes(u)) return "PH";
              if (["WOO", "WEEKLY OFF"].includes(u)) return "WO";
              if (["LO", "LWP"].includes(u)) return "LOP";
              return null;
            };
            // Resolve attendance: backend may key by id, employeeId, empCode, code (number or string)
            let empAttendance = {};
            const idsToTry = [
              employee.id,
              employee.employeeId,
              employee.empCode,
              employee.code,
              employee.emp_code,
              employee.employee_id,
              String(employee.id ?? ""),
              String(employee.employeeId ?? ""),
              String(employee.empCode ?? ""),
              String(employee.code ?? ""),
              employee.id != null ? Number(employee.id) : null,
              employee.employeeId != null ? Number(employee.employeeId) : null,
            ].filter((v) => v !== undefined && v !== null && v !== "" && !Number.isNaN(v));
            // First pass: direct key lookup
            for (const id of idsToTry) {
              if (monthlyAttendance[id] != null && typeof monthlyAttendance[id] === "object") {
                empAttendance = monthlyAttendance[id];
                break;
              }
            }
            // Second pass: iterate all keys and match loosely
            if (Object.keys(empAttendance).length === 0 && typeof monthlyAttendance === "object") {
              const empIdSet = new Set(idsToTry.map((v) => String(v).toLowerCase()));
              for (const key of Object.keys(monthlyAttendance)) {
                const val = monthlyAttendance[key];
                if (!val || typeof val !== "object") continue;
                const keyLower = String(key).toLowerCase();
                if (empIdSet.has(keyLower)) {
                  empAttendance = val;
                  break;
                }
              }
            }
            // Third pass: if still not found and employee has name, try name-based match (last resort)
            if (Object.keys(empAttendance).length === 0 && typeof monthlyAttendance === "object") {
              const empName = (employee.user?.name || employee.name || "").toLowerCase().trim();
              if (empName) {
                for (const key of Object.keys(monthlyAttendance)) {
                  const val = monthlyAttendance[key];
                  if (!val || typeof val !== "object") continue;
                  // Check if the key could be a name match (unlikely but safe)
                  if (String(key).toLowerCase().trim() === empName) {
                    empAttendance = val;
                    break;
                  }
                }
              }
            }
            // Parse employee join/relieved dates for day-level filtering
            const parseEmpDateForDay = (val) => {
              if (val == null || val === "") return null;
              const d = new Date(val);
              return isNaN(d.getTime()) ? null : d;
            };
            const empJoinDate = parseEmpDateForDay(employee.joinDate || employee.joiningDate);
            const empRelievedDate = parseEmpDateForDay(
              employee.relievedDate || employee.exitDate || employee.relievingDate || employee.resignationDate
            );
            // For each day, calculate status: explicit record > default (WO for Sunday, PR otherwise)
            for (let day = 1; day <= daysInMonth; day++) {
              // Check if employee was active on this day
              const currentDate = new Date(Number(y), Number(m) - 1, day);
              if (empJoinDate && currentDate < empJoinDate) continue; // Before join date
              if (empRelievedDate && currentDate > empRelievedDate) continue; // After relieved date
              // Get explicit status for this day
              let status = empAttendance[String(day)] || empAttendance[day] || "";
              // If no explicit status, calculate default: Sunday (0) = WO, other days = PR
              if (!status) {
                const dayOfWeek = currentDate.getDay(); // 0 = Sunday, 6 = Saturday
                status = dayOfWeek === 0 ? "WO" : "PR"; // Sunday = Weekly Off, others = Present
              }
              const raw = String(status).trim().toUpperCase();
              // Half-day: slash-separated (e.g. PR/LOP) – add 0.5 to each part
              if (raw.includes("/")) {
                const parts = raw.split("/").map((p) => normalizeStatusForCount(p)).filter(Boolean);
                parts.forEach((key) => {
                  if (counts[key] !== undefined) counts[key] += 0.5;
                });
                continue;
              }
              // Combined without slash (e.g. PRLOP, WOPR) – try split into two known codes
              if (raw.length > 3) {
                let k1 = null;
                let k2 = null;
                for (let i = 1; i < raw.length; i++) {
                  const left = raw.substring(0, i);
                  const right = raw.substring(i);
                  k1 = normalizeStatusForCount(left);
                  k2 = normalizeStatusForCount(right);
                  if (k1 && k2 && counts[k1] !== undefined && counts[k2] !== undefined) break;
                }
                if (k1 && k2 && counts[k1] !== undefined && counts[k2] !== undefined) {
                  counts[k1] += 0.5;
                  counts[k2] += 0.5;
                  continue;
                }
              }
              const key = normalizeStatusForCount(raw);
              if (key && counts[key] !== undefined) counts[key] += 1;
            }
            // One column per status (label from statusDefinitions); match "calculate and show summary" order
            statusCodesOrder.forEach((code) => {
              const label = statusDefinitions[code];
              if (label) row[label] = counts[code] ?? 0;
            });
            // Pay Days = sum of paid types only (PR, PH, WO + leave types where !isUnpaid)
            let payDaysSum = 0;
            paidCodes.forEach((code) => {
              if (counts[code] !== undefined) payDaysSum += counts[code];
            });
            row["Pay Days"] = payDaysSum;
          }

          return row;
        });

        // Create workbook and worksheet
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet([]);

        // Prepend meta rows
        const metaRows = [
          [isDaily ? "Daily Attendance Report" : "Monthly Attendance Report"],
          ["Company", companyName],
          ["Address", companyAddress],
          [isDaily ? "Date Range" : "Month", dateRangeLabel],
          ["Filters", filterSummary.length > 0 ? filterSummary.join(", ") : "None"],
          [],
        ];
        XLSX.utils.sheet_add_aoa(worksheet, metaRows, { origin: "A1" });

        // Add data rows after meta
        XLSX.utils.sheet_add_json(worksheet, exportData, {
          origin: -1,
          skipHeader: false,
        });

        // Set column widths to auto-fit based on content
        const colWidths = Object.keys(exportData[0] || {}).map((key, index) => {
          const maxLength = Math.max(
            key.length, // Header length
            ...exportData.map((row) => String(row[key] || "").length) // Content length
          );
          return { wch: Math.min(Math.max(maxLength + 2, 10), 50) }; // Min 10, Max 50
        });
        worksheet["!cols"] = colWidths;

        // Style all cells with font and vertical alignment
        const range = XLSX.utils.decode_range(worksheet["!ref"]);
        for (let row = range.s.r; row <= range.e.r; row++) {
          for (let col = range.s.c; col <= range.e.c; col++) {
            const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
            if (!worksheet[cellAddress]) continue;

            const isHeader = row === 0;

            // Set cell style
            worksheet[cellAddress].s = {
              font: {
                name: "Segoe UI",
                sz: 10,
                bold: isHeader,
                color: { rgb: "000000" },
              },
              fill: isHeader
                ? {
                    patternType: "solid",
                    fgColor: { rgb: "F0F0F0" },
                  }
                : undefined,
              alignment: {
                horizontal: "left",
                vertical: "center",
                wrapText: true,
              },
            };
          }
        }

        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(workbook, worksheet, reportType);

        // Generate filename (include date range for daily, month for monthly)
        let dateSuffix = "";
        if (isDaily && dateRange.from && dateRange.to) {
          dateSuffix = `_${dateRange.from}_to_${dateRange.to}`;
        } else if (isMonthly && selectedMonth) {
          const [y, m] = selectedMonth.split("-");
          const monthName = new Date(Number(y), Number(m) - 1, 1).toLocaleString("en-US", {
            month: "long",
            year: "numeric",
          });
          dateSuffix = `_${monthName.replace(/\s+/g, "-")}`;
        }
        const filename = buildExcelFilename(
          `${reportType.replace(/\s+/g, "_")}${dateSuffix}`,
          isMonthly ? selectedMonth : null
        );

        // Write and download
        XLSX.writeFile(workbook, filename);

        toast.success(`${reportType} downloaded successfully!`);
      } catch (error) {
        console.error("Export error:", error);
        toast.error("Failed to download report. Please try again.");
      }
    };

    // Use the shared Filters component (minimal props)
    const applyClientFilters = (filters) => {
      if (!baseEmployees || baseEmployees.length === 0) return [];
      return baseEmployees.filter((emp) => {
        const matchText = (text, target) =>
          !text ||
          (target || "")
            .toString()
            .toLowerCase()
            .includes(text.toString().toLowerCase());

        const matchesSearch =
          !filters.search ||
          matchText(filters.search, emp.user?.name || emp.name || emp.empCode);
        const matchesBranch =
          !filters.branch ||
          emp.branchId === Number(filters.branch) ||
          emp.branch?.id === Number(filters.branch);
        const matchesDepartment =
          !filters.department ||
          emp.departmentId === Number(filters.department) ||
          emp.department?.id === Number(filters.department);
        const matchesDesignation =
          !filters.designation ||
          emp.designationId === Number(filters.designation) ||
          emp.designation?.id === Number(filters.designation);
        const matchesGrade =
          !filters.grade ||
          emp.gradeId === Number(filters.grade) ||
          emp.grade?.id === Number(filters.grade);
        const matchesCategory =
          !filters.category ||
          emp.categoryId === Number(filters.category) ||
          emp.category?.id === Number(filters.category);
        const matchesStatus =
          !filters.status ||
          (emp.employmentStatus || "").toLowerCase() ===
            filters.status.toLowerCase();

        return (
          matchesSearch &&
          matchesBranch &&
          matchesDepartment &&
          matchesDesignation &&
          matchesGrade &&
          matchesCategory &&
          matchesStatus
        );
      });
    };

    const renderFilterPopup = () =>
      showFilterPopup && (
        <Filters
          onApply={(employeesList, filters) => {
            const nextRange = {
              from: filters?.reportDateFrom || dateRange.from,
              to: filters?.reportDateTo || dateRange.to,
            };
            setDateRange(nextRange);
            setAppliedFilters(filters || null);
            setFiltersApplied(true);
            setMonthlyLoaded(false);
            let nextEmployees = employeesList;

            // For daily and monthly attendance, filter the already-loaded base employees locally
            if (activeSubTab === "daily-attendance" || activeSubTab === "monthly-attendance") {
              nextEmployees = applyClientFilters(filters || {});
              setIsLoaded(true);
              // For monthly, reload attendance for filtered employees if month is already loaded
              if (activeSubTab === "monthly-attendance" && monthlyLoaded && selectedMonth) {
                const [y, m] = selectedMonth.split("-");
                const monthNum = Number(m);
                const yearNum = Number(y);
                if (monthNum && yearNum) {
                  const filteredIds = nextEmployees.map((e) => e.id || e.employeeId || e.empCode).filter(Boolean);
                  attendanceService.getAttendanceHistory(
                    monthNum,
                    yearNum,
                    1,
                    50000,
                    "",
                    filteredIds
                  ).then((resp) => {
                    let att = resp?.data || resp?.attendance || {};
                    if (Array.isArray(att)) {
                      const map = {};
                      att.forEach((rec) => {
                        const empId = String(rec.employeeId || rec.employee_id || rec.id);
                        const day = String(new Date(rec.date).getDate());
                        if (!map[empId]) map[empId] = {};
                        map[empId][day] = rec.status;
                      });
                      att = map;
                    }
                    setMonthlyAttendance(att || {});
                  }).catch((err) => {
                    console.error("Failed to reload monthly attendance after filter", err);
                  });
                }
              }
            } else {
              // fallback to API result if provided
              if (!nextEmployees) nextEmployees = [];
              setShowEmployeeList(true);
            }

            setLoadedEmployees(nextEmployees || []);
            setShowEmployeeList(activeSubTab !== "daily-attendance" && activeSubTab !== "monthly-attendance");
            setShowFilterPopup(false);
          }}
          onClose={() => setShowFilterPopup(false)}
          darkMode={darkMode}
          initialFilters={{
            dateFilterType: "custom",
            reportDateFrom: dateRange.from || undefined,
            reportDateTo: dateRange.to || undefined,
          }}
          maxDateRangeDays={activeSubTab === "daily-attendance" ? 31 : undefined}
          returnOnlyFilters={activeSubTab === "daily-attendance"}
        />
      );

    return (
      <div className="flex-1 overflow-auto">
        <div
          className={`max-h-full ${
            darkMode ? "bg-[#1C1C1C] text-[#E0E0E0]" : "bg-white text-gray-900"
          }`}
        >
          <div className="flex h-full">
            {/* Vertical Sidebar */}
            {!hideSidebar && (
              <div
                className={`w-80 border-r ${
                  darkMode
                    ? "bg-[#2C2C2C] border-[#3C3C3C]"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
              <div className="p-6">
                <h1
                  className={`text-2xl font-bold mb-8 ${
                    darkMode ? "text-[#E0E0E0]" : "text-gray-900"
                  }`}
                >
                  Attendance Reports
                </h1>

                {/* Sub-tabs for attendance reports */}
                <div className="space-y-1">
                  <h3
                    className={`text-sm font-semibold mb-3 ${
                      darkMode ? "text-[#A6A9C8]" : "text-gray-500"
                    }`}
                  >
                    ATTENDANCE REPORTS
                  </h3>
                  {ATTENDANCE_REPORT_SIDEBAR.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => handleSubTabSelect(tab.id)}
                      className={`w-full flex items-center px-4 py-2 rounded-md text-left text-sm transition-colors ${
                        activeSubTab === tab.id
                          ? `${
                              darkMode
                                ? "bg-[#3C3C3C] text-redhalo-mint"
                                : "bg-redhalo-mint/40 text-redhalo-navy"
                            }`
                          : `${
                              darkMode
                                ? "text-[#A6A9C8] hover:bg-[#3C3C3C]"
                                : "text-gray-600 hover:bg-gray-100"
                            }`
                      }`}
                    >
                      <tab.icon size={16} className="mr-3" />
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            )}

            {/* Main Content Area */}
            <div className="flex-1 p-6">
              {/* Report Content */}
              <div
                className={`rounded-md shadow-lg ${
                  darkMode
                    ? "bg-[#2C2C2C] border border-[#3C3C3C]"
                    : "bg-white border border-gray-200"
                }`}
              >
                <div className="p-6 border-b border-gray-200">
                  <div className="flex flex-wrap gap-3 items-center justify-between">
                    <div className="min-w-[220px]">
                      <h2
                        className={`text-2xl font-bold ${
                          darkMode ? "text-[#E0E0E0]" : "text-gray-900"
                        }`}
                      >
                        {ATTENDANCE_REPORT_SIDEBAR.find((tab) => tab.id === activeSubTab)?.label ||
                          "Attendance Report"}
                      </h2>
                      <p
                        className={`text-sm mt-1 ${
                          darkMode ? "text-[#A6A9C8]" : "text-gray-600"
                        }`}
                      >
                        Generate and download attendance reports
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      {!FILTERED_PANEL_REPORTS.has(activeSubTab) && activeSubTab === "daily-attendance" && (
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="flex items-center space-x-1">
                            <span className="text-sm">From</span>
                            <DatePicker
                              value={dateRange.from || ""}
                              onChange={(e) =>
                                setDateRange((prev) => ({
                                  ...prev,
                                  from: String(e?.target?.value || ""),
                                }))
                              }
                              min={dailyFromMinISO}
                              max={dailyFromMaxISO}
                              darkMode={darkMode}
                              dropdownAlign="auto"
                              className="z-50 min-w-[140px]"
                            />
                          </div>
                          <div className="flex items-center space-x-1">
                            <span className="text-sm">To</span>
                            <DatePicker
                              value={dateRange.to || ""}
                              onChange={(e) =>
                                setDateRange((prev) => ({
                                  ...prev,
                                  to: String(e?.target?.value || ""),
                                }))
                              }
                              min={dailyToMinISO}
                              max={dailyToMaxISO}
                              darkMode={darkMode}
                              dropdownAlign="auto"
                              className="z-50 min-w-[140px]"
                            />
                          </div>
                          <button
                            onClick={loadAttendanceData}
                            className={`flex items-center px-4 py-2 rounded-md font-medium transition-colors ${
                              darkMode
                                ? "bg-[#3C3C3C] text-[#E0E0E0] hover:bg-[#4C4C4C]"
                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                          >
                            Load
                          </button>
                        </div>
                      )}
                      {activeSubTab === "monthly-attendance" && (
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="flex items-center space-x-1">
                            <span className="text-sm">Month</span>
                            <DatePicker
                              monthYearOnly
                              showMonthName
                              monthYearEmptyLabel="Select month"
                              value={selectedMonth ? `${selectedMonth}-01` : ""}
                              onChange={(e) => {
                                const parsed = parseYearMonthFromPickerValue(e?.target?.value);
                                if (!parsed) {
                                  setSelectedMonth("");
                                  setMonthlyLoaded(false);
                                  setIsLoaded(false);
                                  return;
                                }
                                const next = `${parsed.year}-${String(parsed.month).padStart(2, "0")}`;
                                setSelectedMonth(next);
                                setMonthlyLoaded(false);
                                setIsLoaded(false);
                              }}
                              min={lopReversalMinDate || undefined}
                              max={new Date()}
                              darkMode={darkMode}
                              dropdownAlign="auto"
                              className="z-50 min-w-[180px]"
                            />
                          </div>
                          <button
                            onClick={loadAttendanceData}
                            className={`flex items-center px-4 py-2 rounded-md font-medium transition-colors ${
                              darkMode
                                ? "bg-[#3C3C3C] text-[#E0E0E0] hover:bg-[#4C4C4C]"
                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                          >
                            Load
                          </button>
                        </div>
                      )}
                      {activeSubTab === "comp-off-ledger" && (
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="flex items-center space-x-1">
                            <span className="text-sm">From</span>
                            <DatePicker
                              value={compOffDateRange.from || ""}
                              onChange={(e) =>
                                setCompOffDateRange((prev) => ({
                                  ...prev,
                                  from: String(e?.target?.value || ""),
                                }))
                              }
                              min={companyMinISO}
                              max={compOffDateRange.to || todayISO}
                              darkMode={darkMode}
                              dropdownAlign="auto"
                              className="z-50 min-w-[140px]"
                            />
                          </div>
                          <div className="flex items-center space-x-1">
                            <span className="text-sm">To</span>
                            <DatePicker
                              value={compOffDateRange.to || ""}
                              onChange={(e) =>
                                setCompOffDateRange((prev) => ({
                                  ...prev,
                                  to: String(e?.target?.value || ""),
                                }))
                              }
                              min={compOffDateRange.from || companyMinISO}
                              max={todayISO}
                              darkMode={darkMode}
                              dropdownAlign="auto"
                              className="z-50 min-w-[140px]"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              loadCompOffLedgerReport(
                                compOffDateRange.from,
                                compOffDateRange.to
                              )
                            }
                            className={`flex items-center px-4 py-2 rounded-md font-medium transition-colors ${
                              darkMode
                                ? "bg-[#3C3C3C] text-[#E0E0E0] hover:bg-[#4C4C4C]"
                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                          >
                            Load
                          </button>
                          {compOffLedgerLoading && (
                            <span className={`text-sm ${darkMode ? "text-[#A6A9C8]" : "text-gray-500"}`}>
                              Loading…
                            </span>
                          )}
                        </div>
                      )}
                      {activeSubTab === "lop-reversal-report" && (
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="flex items-center space-x-1">
                            <span className="text-sm">Payroll Month</span>
                            <DatePicker
                              monthYearOnly
                              showMonthName
                              monthYearEmptyLabel="Select month"
                              value={
                                lopReversalReportYear != null && lopReversalReportMonth != null
                                  ? `${lopReversalReportYear}-${String(lopReversalReportMonth).padStart(2, "0")}-01`
                                  : ""
                              }
                              onChange={(e) => {
                                const raw = e?.target?.value;
                                if (raw == null || String(raw).trim() === "") {
                                  setLOPReversalReportYear(null);
                                  setLOPReversalReportMonth(null);
                                  return;
                                }
                                const parsed = parseYearMonthFromPickerValue(raw);
                                if (parsed) {
                                  setLOPReversalReportYear(parsed.year);
                                  setLOPReversalReportMonth(parsed.month);
                                }
                              }}
                              min={lopReversalMinDate || undefined}
                              max={new Date()}
                              darkMode={darkMode}
                              dropdownAlign="auto"
                              className="z-50 min-w-[200px]"
                            />
                          </div>
                          {lopReversalReportLoading && (
                            <span className={`text-sm ${darkMode ? "text-[#A6A9C8]" : "text-gray-500"}`}>
                              Loading…
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions and Summary */}
                <div
                  className={`px-6 py-4 flex flex-wrap items-center justify-between border-b ${
                    darkMode ? "border-[#3C3C3C]" : "border-gray-200"
                  }`}
                >
                  <div className="space-y-1">
                    <div className="font-semibold">
                      {activeSubTab === "daily-attendance"
                        ? "Overall Summary"
                        : activeSubTab === "monthly-attendance"
                        ? "Monthly Summary"
                        : activeSubTab === "lop-reversal-report"
                        ? "LOP Reversal Summary"
                        : activeSubTab === "comp-off-ledger"
                        ? "Comp Off Ledger Summary"
                        : "Employee Summary"}
                    </div>
                    <div className="text-sm">
                      {activeSubTab === "daily-attendance" ? (
                        <>
                          Date range:{" "}
                          <span className="font-semibold">
                            {dateRange.from
                              ? new Date(dateRange.from).toLocaleDateString()
                              : "—"}{" "}
                            to{" "}
                            {dateRange.to
                              ? new Date(dateRange.to).toLocaleDateString()
                              : "—"}
                          </span>
                          <span className="ml-3">
                            Employees:{" "}
                            <span className="font-semibold">
                              {isLoaded ? loadedEmployees.length : "--"}
                            </span>
                          </span>
                        </>
                      ) : activeSubTab === "monthly-attendance" ? (
                        <>
                          Month:{" "}
                          <span className="font-semibold">
                            {selectedMonth
                              ? (() => {
                                  const [y, m] = selectedMonth.split("-");
                                  return `${new Date(
                                    Number(y),
                                    Number(m) - 1,
                                    1
                                  ).toLocaleString("en-US", {
                                    month: "long",
                                    year: "numeric",
                                  })}`;
                                })()
                              : "—"}
                          </span>
                          <span className="ml-3">
                            Employees:{" "}
                            <span className="font-semibold">
                              {isLoaded ? loadedEmployees.length : "--"}
                            </span>
                          </span>
                        </>
                      ) : activeSubTab === "lop-reversal-report" ? (
                        <>
                          Payroll month:{" "}
                          <span className="font-semibold">
                            {lopReversalReportYear != null && lopReversalReportMonth != null
                              ? new Date(lopReversalReportYear, lopReversalReportMonth - 1, 1).toLocaleString("default", {
                                  month: "long",
                                  year: "numeric",
                                })
                              : "—"}
                          </span>
                          <span className="ml-3">
                            Employees with LOP reversal:{" "}
                            <span className="font-semibold">
                              {lopReversalReportMonth != null && lopReversalReportYear != null
                                ? lopReversalReportData.length
                                : "—"}
                            </span>
                          </span>
                        </>
                      ) : activeSubTab === "comp-off-ledger" ? (
                        <>
                          Lots:{" "}
                          <span className="font-semibold">
                            {compOffLedgerFetched ? compOffLedgerRows.length : "--"}
                          </span>
                          <span className="ml-3">
                            Worked date:{" "}
                            <span className="font-semibold">
                              {compOffDateRange.from || "All"} to {compOffDateRange.to || "All"}
                            </span>
                          </span>
                        </>
                      ) : (
                        <>
                          Employees:{" "}
                          <span className="font-semibold">
                            {loadedEmployees.length}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  {!FILTERED_PANEL_REPORTS.has(activeSubTab) && (
                  <div className="flex flex-wrap items-center gap-3">
                    {activeSubTab !== "comp-off-ledger" && (
                    <button
                      onClick={() => setShowFilterPopup(true)}
                      className={`flex items-center px-4 py-2 rounded-md font-medium transition-colors ${
                        darkMode
                          ? "bg-[#3C3C3C] text-[#E0E0E0] hover:bg-[#4C4C4C]"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      <Filter size={18} className="mr-2" />
                      Filters
                    </button>
                    )}
                    <button
                      onClick={() =>
                        exportReport(
                          [
                            {
                              id: "daily-attendance",
                              label: "Daily Attendance Report",
                            },
                            {
                              id: "monthly-attendance",
                              label: "Monthly Attendance Report",
                            },
                            { id: "leave-report", label: "Leave Report" },
                            {
                              id: "overtime-report",
                              label: "Overtime Report",
                            },
                            { id: "lop-reversal-report", label: "LOP Reversal Report" },
                            { id: "comp-off-ledger", label: "Comp Off Ledger Report" },
                          ].find((tab) => tab.id === activeSubTab)?.label
                        )
                      }
                      className={`flex items-center px-6 py-2 rounded-md font-medium transition-colors ${
                        darkMode
                          ? "bg-redhalo-navy text-white hover:bg-redhalo-navy-light"
                          : "rh-btn-primary hover:opacity-95"
                      }`}
                    >
                      <Download size={18} className="mr-2" />
                      Download
                    </button>
                  </div>
                  )}
                </div>

                {FILTERED_PANEL_REPORTS.has(activeSubTab) && (
                  <div className="flex min-h-[520px] flex-col p-6">
                    <AttendanceFilteredReportPanel
                      reportKey={
                        activeSubTab === "leave-report"
                          ? "leave-balance-report"
                          : activeSubTab
                      }
                      darkMode={darkMode}
                    />
                  </div>
                )}

                {/* LOP Reversal: prompt until payroll month is chosen */}
                {!FILTERED_PANEL_REPORTS.has(activeSubTab) &&
                activeSubTab === "lop-reversal-report" &&
                  (lopReversalReportMonth == null || lopReversalReportYear == null) &&
                  !lopReversalReportLoading && (
                    <div className="p-6">
                      <div
                        className={`text-center py-12 ${
                          darkMode ? "text-[#A6A9C8]" : "text-gray-500"
                        }`}
                      >
                        <div className="mb-4">
                          <FileText
                            size={48}
                            className={`mx-auto ${
                              darkMode ? "text-[#4C4C4C]" : "text-gray-300"
                            }`}
                          />
                        </div>
                        <h3 className="text-lg font-medium mb-2">Ready to Load Employee Data</h3>
                        <p className="text-sm mb-4 max-w-lg mx-auto">
                          Open the filters, select your preferred options, and click Apply to view the employee list.
                        </p>
                        <p className="text-xs text-gray-500 max-w-lg mx-auto">
                          After loading the data, you can export the report. Select a payroll month above to load the
                          LOP reversal report.
                        </p>
                      </div>
                    </div>
                  )}

                {/* LOP Reversal Report: empty fetch / table */}
                {activeSubTab === "lop-reversal-report" &&
                  lopReversalReportMonth != null &&
                  lopReversalReportYear != null &&
                  !lopReversalReportLoading &&
                  lopReversalReportFetched &&
                  lopReversalReportData.length === 0 && (
                    <div
                      className={`p-10 text-center text-sm ${
                        darkMode ? "text-[#A6A9C8]" : "text-gray-600"
                      }`}
                    >
                      No LOP reversal records for{" "}
                      <span className="font-semibold">
                        {new Date(lopReversalReportYear, lopReversalReportMonth - 1, 1).toLocaleString("default", {
                          month: "long",
                          year: "numeric",
                        })}
                      </span>
                      .
                    </div>
                  )}

                {/* LOP Reversal Report table */}
                {activeSubTab === "lop-reversal-report" &&
                  lopReversalReportMonth != null &&
                  lopReversalReportYear != null &&
                  lopReversalReportData.length > 0 && (
                  <div className="p-6 overflow-x-auto">
                    <table className={`w-full text-sm ${darkMode ? "text-[#E0E0E0]" : "text-gray-800"}`}>
                      <thead className={darkMode ? "bg-[#3C3C3C]" : "bg-gray-100"}>
                        <tr>
                          <th className="px-2 py-2 text-left whitespace-nowrap">Sl No</th>
                          <th className="px-2 py-2 text-left whitespace-nowrap">Employee Code</th>
                          <th className="px-2 py-2 text-left whitespace-nowrap">Employee Name</th>
                          <th className="px-2 py-2 text-left whitespace-nowrap">DOJ</th>
                          <th className="px-2 py-2 text-left whitespace-nowrap w-[96px]">LOP Month</th>
                          <th className="px-2 py-2 text-right whitespace-nowrap w-[128px]">LOP Reversal Days</th>
                          <th className="px-2 py-2 text-left min-w-[220px]">Dates</th>
                          <th className="px-2 py-2 text-left whitespace-nowrap">Payroll Month</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lopReversalReportData.map((row, idx) => (
                          <tr key={`${row.employeeId}-${row.lopMonth}-${idx}`} className={darkMode ? "border-b border-[#3C3C3C]" : "border-b border-gray-200"}>
                            <td className="px-2 py-2">{idx + 1}</td>
                            <td className="px-2 py-2">{row.employeeCode}</td>
                            <td className="px-2 py-2">{row.employeeName}</td>
                            <td className="px-2 py-2">{row.doj ? String(row.doj).slice(0, 10) : ""}</td>
                            <td className="px-2 py-2 whitespace-nowrap w-[96px]">{row.lopMonth}</td>
                            <td className="px-2 py-2 text-right font-medium whitespace-nowrap w-[128px]">{row.lopReversalDays}</td>
                            <td className="px-2 py-2 min-w-[220px]">{row.dates}</td>
                            <td className="px-2 py-2">{row.payrollMonth}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {activeSubTab === "comp-off-ledger" &&
                  !compOffLedgerLoading &&
                  compOffLedgerFetched &&
                  compOffLedgerRows.length === 0 && (
                    <div
                      className={`p-10 text-center text-sm ${
                        darkMode ? "text-[#A6A9C8]" : "text-gray-600"
                      }`}
                    >
                      No Comp Off earn or avail lots for the selected period.
                    </div>
                  )}

                {activeSubTab === "comp-off-ledger" && compOffLedgerRows.length > 0 && (
                  <div className="p-6 overflow-x-auto">
                    <table className={`w-full text-sm ${darkMode ? "text-[#E0E0E0]" : "text-gray-800"}`}>
                      <thead className={darkMode ? "bg-[#3C3C3C]" : "bg-gray-100"}>
                        <tr>
                          <th className="px-2 py-2 text-left whitespace-nowrap">Sl No</th>
                          <th className="px-2 py-2 text-left whitespace-nowrap">Employee Code</th>
                          <th className="px-2 py-2 text-left whitespace-nowrap">Employee Name</th>
                          <th className="px-2 py-2 text-left whitespace-nowrap">Type</th>
                          <th className="px-2 py-2 text-left whitespace-nowrap">Worked Date</th>
                          <th className="px-2 py-2 text-left whitespace-nowrap">Source</th>
                          <th className="px-2 py-2 text-right whitespace-nowrap">Hours</th>
                          <th className="px-2 py-2 text-right whitespace-nowrap">Days</th>
                          <th className="px-2 py-2 text-left whitespace-nowrap">Expires On</th>
                          <th className="px-2 py-2 text-left whitespace-nowrap">Availed On</th>
                          <th className="px-2 py-2 text-right whitespace-nowrap">Remaining</th>
                          <th className="px-2 py-2 text-left whitespace-nowrap">Status</th>
                          <th className="px-2 py-2 text-left">Remarks</th>
                        </tr>
                      </thead>
                      <tbody>
                        {compOffLedgerRows.map((row, idx) => (
                          <tr
                            key={row.id || `${row.employeeId}-${row.sourceDate}-${idx}`}
                            className={darkMode ? "border-b border-[#3C3C3C]" : "border-b border-gray-200"}
                          >
                            <td className="px-2 py-2">{idx + 1}</td>
                            <td className="px-2 py-2">{row.employeeCode}</td>
                            <td className="px-2 py-2">{row.employeeName}</td>
                            <td className="px-2 py-2">
                              {row.movement === "availed" ? "Availed" : "Earned"}
                            </td>
                            <td className="px-2 py-2 whitespace-nowrap">
                              {formatCompOffDate(row.sourceDate)}
                            </td>
                            <td className="px-2 py-2">{formatCompOffSource(row.sourceType)}</td>
                            <td className="px-2 py-2 text-right">
                              {row.hoursWorked == null ? "—" : formatCompOffDays(row.hoursWorked)}
                            </td>
                            <td className="px-2 py-2 text-right font-medium">
                              {formatCompOffDays(row.creditDays)}
                            </td>
                            <td className="px-2 py-2 whitespace-nowrap">
                              {formatCompOffDate(row.expiresOn)}
                            </td>
                            <td className="px-2 py-2 whitespace-nowrap">
                              {formatCompOffDate(row.availDate)}
                            </td>
                            <td className="px-2 py-2 text-right">
                              {formatCompOffDays(row.remainingDays)}
                            </td>
                            <td className="px-2 py-2">{formatCompOffStatus(row)}</td>
                            <td className="px-2 py-2">{row.notes || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Employee List Container - Only show when data is loaded (not for daily, monthly, or lop-reversal) */}
                {showEmployeeList && activeSubTab !== "daily-attendance" && activeSubTab !== "monthly-attendance" && activeSubTab !== "lop-reversal-report" && activeSubTab !== "comp-off-ledger" && (
                  <div className="employee-list-container flex-1 flex flex-col h-[600px]">
                    <div className="flex-shrink-0">
                      <div
                        className={`px-4 pt-4 pb-2 ${
                          darkMode ? "bg-[#1C1C1C]" : "bg-white"
                        } flex items-center justify-between`}
                      >
                        <h3
                          className={`text-lg font-semibold ${
                            darkMode ? "text-[#E0E0E0]" : "text-gray-900"
                          }`}
                        >
                          Employee List ({loadedEmployees.length} employees)
                        </h3>
                      </div>
                    </div>

                    <div className="flex-1 overflow-hidden">
                      <div className="h-full overflow-y-auto">
                        <table
                          className={`w-full text-sm employee-table ${
                            darkMode
                              ? "bg-[#2C2C2C] text-[#E0E0E0]"
                              : "bg-white text-gray-900"
                          }`}
                        >
                          <thead
                            className={`${
                              darkMode ? "bg-[#3C3C3C]" : "bg-gray-100"
                            } sticky top-0 z-40`}
                          >
                            <tr>
                              <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                SL No
                              </th>
                              <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                Employee Code
                              </th>
                              <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                Name
                              </th>
                              <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                Department
                              </th>
                              <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                Designation
                              </th>
                              <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                Status
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {loadedEmployees.length > 0 ? (
                              loadedEmployees.map((employee, index) => (
                                <tr
                                  key={employee.id}
                                  className={`border-b ${
                                    darkMode
                                      ? "border-[#3C3C3C] hover:bg-[#3C3C3C]"
                                      : "border-gray-200 hover:bg-gray-50"
                                  }`}
                                >
                                  <td className="px-2 py-2 text-center">
                                    {index + 1}
                                  </td>
                                  <td className="px-2 py-2 text-center font-medium">
                                    {employee.empCode}
                                  </td>
                                  <td className="px-2 py-2 text-center">
                                    {employee.user?.name || employee.name}
                                  </td>
                                  <td className="px-2 py-2 text-center">
                                    {employee.department?.name || "-"}
                                  </td>
                                  <td className="px-2 py-2 text-center">
                                    {employee.designation?.name || "-"}
                                  </td>
                                  <td className="px-2 py-2 text-center">
                                    <span
                                      className={`px-2 py-1 rounded-md text-xs ${
                                        employee.employmentStatus === "active"
                                          ? "bg-green-100 text-green-800"
                                          : "bg-red-100 text-red-800"
                                      }`}
                                    >
                                      {employee.employmentStatus || "Active"}
                                    </span>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td
                                  colSpan="6"
                                  className="px-4 py-8 text-center text-gray-500"
                                >
                                  No employees found matching the current
                                  filters.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {/* Initial State - Show when no data is loaded (not LOP Reversal — that auto-loads) */}
                {!showEmployeeList && activeSubTab !== "lop-reversal-report" && activeSubTab !== "comp-off-ledger" && (
                  <div className="p-6">
                    <div
                      className={`text-center py-12 ${
                        darkMode ? "text-[#A6A9C8]" : "text-gray-500"
                      }`}
                    >
                      <div className="mb-4">
                        <FileText
                          size={48}
                          className={`mx-auto ${
                            darkMode ? "text-[#4C4C4C]" : "text-gray-300"
                          }`}
                        />
                      </div>
                      <h3 className="text-lg font-medium mb-2">
                        Ready to Load Employee Data
                      </h3>
                      {activeSubTab === "daily-attendance" ? (
                        <>
                          {isLoaded ? (
                            <div className="space-y-2 text-sm">
                              <div>
                                Employees included:{" "}
                                <span className="font-semibold">
                                  {loadedEmployees.length}
                                </span>
                              </div>
                              <div>
                                Date range:{" "}
                                <span className="font-semibold">
                                  {dateRange.from
                                    ? new Date(dateRange.from).toLocaleDateString()
                                    : "—"}{" "}
                                  to{" "}
                                  {dateRange.to
                                    ? new Date(dateRange.to).toLocaleDateString()
                                    : "—"}
                                </span>
                              </div>
                              <div className="text-xs text-gray-500">
                                Click Download to generate the attendance report.
                              </div>
                            </div>
                          ) : (
                            <p className="text-xs text-gray-500">
                              Select date range and click Load to see employee
                              summary.
                            </p>
                          )}
                        </>
                      ) : activeSubTab === "monthly-attendance" ? (
                        <>
                          <p className="text-sm mb-4">
                            Choose a month, click Load, then apply filters if
                            needed. Download the report without loading the full
                            list.
                          </p>
                          {isLoaded ? (
                            <div className="space-y-2 text-sm">
                              <div>
                                Employees included:{" "}
                                <span className="font-semibold">
                                  {loadedEmployees.length}
                                </span>
                              </div>
                              <div>
                                Month:{" "}
                                <span className="font-semibold">
                                  {selectedMonth
                                    ? (() => {
                                        const [y, m] = selectedMonth.split("-");
                                        return `${new Date(
                                          Number(y),
                                          Number(m) - 1,
                                          1
                                        ).toLocaleString("en-US", {
                                          month: "long",
                                          year: "numeric",
                                        })}`;
                                      })()
                                    : "—"}
                                </span>
                              </div>
                              <div className="text-xs text-gray-500">
                                Click Download to generate the attendance report
                                with summary totals.
                              </div>
                            </div>
                          ) : (
                            <p className="text-xs text-gray-500">
                              Choose a month and click Load to see employee
                              summary.
                            </p>
                          )}
                        </>
                      ) : (
                        <>
                          <p className="text-sm mb-4">
                            Open the filters, select your preferred options, and
                            click Apply to view the employee list.
                          </p>
                          <p className="text-xs text-gray-500">
                            After loading the data, you can export the report.
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Filter Popup */}
        {renderFilterPopup()}
      </div>
    );
  },
  (prevProps, nextProps) => {
    return prevProps.darkMode === nextProps.darkMode;
  }
);

export default AttendanceReports;
